import React from 'react';
import clsx from 'clsx';
import { Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';

type AISummaryVariant = 'green' | 'pink';

interface Props {
	summary?: string;
	isLoading?: boolean;
	variant?: AISummaryVariant;
	flaggedReason?: string[] | null;
}

const variantStyles: Record<
	AISummaryVariant,
	{ bg: string; skeleton: string }
> = {
	green: {
		bg: 'bg-green-secondary',
		skeleton: 'bg-green-200',
	},
	pink: {
		bg: 'bg-[#EB35CE14]',
		skeleton: 'bg-pink-200',
	},
};

export const AISummary: React.FC<Props> = ({
	summary,
	isLoading,
	variant = 'green',
	flaggedReason,
}) => {
	if (flaggedReason?.length) {
		return (
			<div className="rounded-2xl p-3 bg-green-secondary flex items-center gap-2">
				<Icon
					src={Icons.flagFilled}
					width={16}
					height={16}
					className="shrink-0 text-[#7C3AED]"
				/>
				<Typography type="regular-14" className="min-w-0 break-words">
					<span className="font-semibold">Flag reason:</span>{' '}
					{flaggedReason.join(', ')}
				</Typography>
			</div>
		);
	}

	if (!isLoading && !summary) return null;

	const styles = variantStyles[variant];

	return (
		<div className={clsx('rounded-2xl p-3', styles.bg)}>
			{isLoading ? (
				<div className="animate-pulse space-y-2">
					<div className={clsx('h-3 w-24 rounded', styles.skeleton)} />
					<div className={clsx('h-3 w-full rounded', styles.skeleton)} />
					<div className={clsx('h-3 w-3/4 rounded', styles.skeleton)} />
				</div>
			) : (
				<Typography type="regular-14" className="break-words">
					<span className="font-semibold">AI Summary:</span> {summary}
				</Typography>
			)}
		</div>
	);
};
